import { toast } from "sonner";

import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn, copyToClipboard } from "@/lib/utils";
import { formatRefDisplay, hasDisplayableId } from "@/lib/shortId";

/**
 * The single canonical style for an identifier shown in the UI.
 *
 * Monospace keeps the characters aligned down a table column, the smaller size
 * marks the value as metadata rather than content, and the muted colour keeps it
 * from competing with the customer name and amount beside it.
 */
const SHORT_ID_CLASSES =
  "font-mono text-xs tracking-wide tabular-nums text-muted-foreground";

interface ShortIdProps {
  /** The database id. Shown as its last 4 characters when no reference exists. */
  id: string | null | undefined;
  /** A human-readable reference (booking ref, invoice number). Wins over the id. */
  reference?: string | null;
  /** Names the value in the tooltip and the copy confirmation, e.g. "Payment ID". */
  label?: string;
  /** Whether clicking copies the full id. Off for ids sitting mid-sentence. */
  copyable?: boolean;
  /** Shown when there is neither a reference nor an id. */
  fallback?: string;
  className?: string;
}

export function ShortId({
  id,
  reference,
  label = "ID",
  copyable = true,
  fallback = "-",
  className,
}: ShortIdProps) {
  const display = formatRefDisplay(reference, id, fallback);

  // Nothing real to show, so render the placeholder without a tooltip or copy button.
  if (!hasDisplayableId(reference, id)) {
    return <span className={cn(SHORT_ID_CLASSES, className)}>{display}</span>;
  }

  // Prefer copying the full id, since that is the value someone needs for a lookup.
  const copyValue = id || reference || "";
  const tooltipValue = id || reference;

  const handleCopy = async (event: React.MouseEvent) => {
    // Table rows and cards often have their own click handlers; copying an id
    // should not also open a detail dialog.
    event.stopPropagation();

    try {
      await copyToClipboard(copyValue);
      toast.success(`${label} copied`);
    } catch {
      toast.error(`Failed to copy ${label.toLowerCase()}`);
    }
  };

  if (!copyable) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={cn(SHORT_ID_CLASSES, className)}>{display}</span>
        </TooltipTrigger>
        <TooltipContent>
          <span className="font-mono">{`${label}: ${tooltipValue}`}</span>
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={handleCopy}
          className={cn(
            SHORT_ID_CLASSES,
            "cursor-pointer rounded transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            className,
          )}
        >
          {display}
        </button>
      </TooltipTrigger>
      <TooltipContent>
        <span className="font-mono">{`${label}: ${tooltipValue}`}</span>
        <span className="ml-2 opacity-70">Click to copy</span>
      </TooltipContent>
    </Tooltip>
  );
}
